
import { TVMInputs } from './types';

export const calculateBalloonPayment = (inputs: TVMInputs): number => {
  const { presentValue, payment, interestRate, paymentsPerYear, paymentsUntilBalloon } = inputs;
  
  if (presentValue <= 0 || payment <= 0 || interestRate < 0 || paymentsUntilBalloon <= 0) return 0;
  
  const monthlyRate = (interestRate / 100) / paymentsPerYear;
  
  if (interestRate === 0) {
    // Handle 0% interest rate case - simple subtraction
    return Math.max(0, presentValue - (payment * paymentsUntilBalloon));
  }
  
  // Calculate remaining balance after specified number of payments
  const remainingBalance = presentValue * Math.pow(1 + monthlyRate, paymentsUntilBalloon) - 
                          payment * ((Math.pow(1 + monthlyRate, paymentsUntilBalloon) - 1) / monthlyRate);
  
  return Math.max(0, remainingBalance);
};

export const calculatePayment = (inputs: TVMInputs): number => {
  const { presentValue, interestRate, periods, paymentsPerYear } = inputs;
  if (presentValue <= 0 || interestRate < 0 || periods <= 0) return 0;
  
  if (interestRate === 0) {
    // Handle 0% interest rate case - simple division
    return presentValue / periods;
  }
  
  const monthlyRate = (interestRate / 100) / paymentsPerYear;
  const totalPayments = periods; // periods is now in months
  
  const payment = (presentValue * monthlyRate * Math.pow(1 + monthlyRate, totalPayments)) / 
                 (Math.pow(1 + monthlyRate, totalPayments) - 1);
  
  return payment;
};

export const calculateRemainingBalance = (inputs: TVMInputs): number => {
  const { presentValue, payment, interestRate, paymentsPerYear } = inputs;
  const paymentsMade = inputs.periods; // periods now represents payments made in months
  
  if (presentValue <= 0 || payment <= 0 || interestRate < 0 || paymentsMade < 0) return 0;
  if (!payment || payment === 0) return 0; // Guard against missing payment
  
  const monthlyRate = (interestRate / 100) / paymentsPerYear;
  
  if (interestRate === 0) {
    // Handle 0% interest rate case
    return Math.max(0, presentValue - (payment * paymentsMade));
  }
  
  // Standard remaining balance formula for amortizing loans
  const balance = presentValue * Math.pow(1 + monthlyRate, paymentsMade) - 
                 payment * ((Math.pow(1 + monthlyRate, paymentsMade) - 1) / monthlyRate);
  
  return Math.max(0, balance);
};

export const calculatePeriods = (inputs: TVMInputs): number => {
  const { presentValue, payment, interestRate, paymentsPerYear } = inputs;
  if (presentValue <= 0 || payment <= 0 || interestRate < 0) return 0;
  
  if (interestRate === 0) {
    // Handle 0% interest rate case - simple division
    return Math.ceil(presentValue / payment);
  }
  
  const monthlyRate = (interestRate / 100) / paymentsPerYear;
  
  // Calculate number of payments needed to pay off the loan
  const periods = -Math.log(1 - (presentValue * monthlyRate) / payment) / 
                 Math.log(1 + monthlyRate);
  
  return Math.ceil(periods); // Return in months
};

// New function to estimate balloon payment from current balance
export const calculateBalloonFromCurrentBalance = (currentBalance: number, monthlyPayment: number, interestRate: number, paymentsUntilBalloon: number, paymentsPerYear: number = 12): number => {
  if (currentBalance <= 0 || monthlyPayment <= 0 || interestRate < 0 || paymentsUntilBalloon <= 0) return 0;
  
  if (interestRate === 0) {
    // Handle 0% interest rate case - simple subtraction
    return Math.max(0, currentBalance - (monthlyPayment * paymentsUntilBalloon));
  }
  
  const monthlyRate = (interestRate / 100) / paymentsPerYear;
  
  // Calculate remaining balance after additional payments
  const balloonAmount = currentBalance * Math.pow(1 + monthlyRate, paymentsUntilBalloon) - 
                       monthlyPayment * ((Math.pow(1 + monthlyRate, paymentsUntilBalloon) - 1) / monthlyRate);
  
  return Math.max(0, balloonAmount);
};
